// src/hooks/useOneDrive.ts
import { useState, useCallback } from "react";
import { oneDriveService, OneDriveId } from "@services/OneDriveService";
import { OneDriveFileUpload } from "./OneDrivePicker";

interface UseOneDriveResult<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  fetchData: (...args: any[]) => Promise<void>;
}

interface UseOneDriveUploadResult<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  /**
   * Upload an array of items (files/folders) from OneDrive
   * @param projectId required (query)
   * @param items required (body)
   * @param reportId optional (query)
   */
  fetchData: (
    projectId: OneDriveId,
    items: OneDriveFileUpload[],
    reportId?: number | null
  ) => Promise<void>;
}

export function useOneDrivePickerFiles<T = any>(): UseOneDriveResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await oneDriveService.getPickerFiles();
      setData(response);
    } catch (err: any) {
      setError(err.message ?? "Failed to fetch picker files");
    } finally {
      setLoading(false);
    }
  }, []);

  return { data, loading, error, fetchData };
}

export function useOneDriveFolderContents<T = any>(
  folderId: OneDriveId
): UseOneDriveResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!folderId) return;
    try {
      setLoading(true);
      setError(null);
      const response = await oneDriveService.getPickerFolderContents(folderId);
      setData(response);
    } catch (err: any) {
      setError(err.message ?? "Failed to fetch folder contents");
    } finally {
      setLoading(false);
    }
  }, [folderId]);

  return { data, loading, error, fetchData };
}

export function useOneDriveUpload<T = any>(): UseOneDriveUploadResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(
    async (projectId: OneDriveId, items: OneDriveFileUpload[]) => {
      if (!projectId) {
        setError("projectId is required");
        return;
      }
      if (!Array.isArray(items) || items.length === 0) {
        setError("At least one item must be provided");
        return;
      }
      try {
        setLoading(true);
        setError(null);
        const response = await oneDriveService.uploadFromDrive(
          projectId,
          items
        );
        setData(response);
      } catch (err: any) {
        setError(err?.message ?? "Failed to upload from OneDrive");
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return { data, loading, error, fetchData };
}

/**
 * Confirmation: The provided code is complete and hasn’t been changed elsewhere.
 */
